import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import './KelolaAkun.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000';

function KelolaAkun({ user, onLogout }) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [localUsers, setLocalUsers] = useState([]);
  const [formData, setFormData] = useState({
    nama: '',
    no_telp: '',
    password: '',
    role: 'kurir'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  // WebSocket refs
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isAuthenticatedRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocalUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Gagal memuat data users');
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, skipping WebSocket connection');
      return;
    }

    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
        console.log('WebSocket already connected or connecting');
        return;
      }
    }

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.log('Max reconnection attempts reached');
      return;
    }

    try {
     
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        
        reconnectAttemptsRef.current = 0;
        isAuthenticatedRef.current = false;
        ws.send(JSON.stringify({ type: 'authenticate', token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'authenticated') {
           
            isAuthenticatedRef.current = true;
          } else if (data.type === 'online_users') {
            setLocalUsers(prevUsers => {
              if (JSON.stringify(prevUsers) !== JSON.stringify(data.users)) {
                return data.users;
              }
              return prevUsers;
            });
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log(`WebSocket closed with code: ${event.code}`);
        wsRef.current = null;
        isAuthenticatedRef.current = false;
        
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        const shouldReconnect = 
          event.code !== 1000 && 
          localStorage.getItem('token') &&
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS;
        
        if (shouldReconnect) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('WebSocket init error:', err);
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    
    const wsTimeout = setTimeout(() => {
      connectWebSocket();
    }, 500);

    return () => {
      clearTimeout(wsTimeout);
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (wsRef.current) {
        const ws = wsRef.current;
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'Component unmounted');
        }
        wsRef.current = null;
      }
      
      reconnectAttemptsRef.current = 0;
      isAuthenticatedRef.current = false;
    };
  }, [fetchUsers, connectWebSocket]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetForm = () => {
    setFormData({
      nama: '',
      no_telp: '',
      password: '',
      role: 'kurir'
    });
    setModalMode('add');
    setSelectedUser(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError('');
    setSuccess('');
  };

  const handleOpenAddModal = () => {
    resetForm();
    setModalMode('add');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      if (modalMode === 'edit') {
        const updateData = {
          nama: formData.nama,
          no_telp: formData.no_telp,
          role: formData.role
        };
        
        if (formData.password) {
          updateData.password = formData.password;
        }

        await axios.put(`${API_URL}/api/users/${selectedUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setSuccess('User berhasil diupdate!');
      } else {
        await axios.post(`${API_URL}/api/register`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setSuccess('User berhasil ditambahkan!');
      }
      
      handleCloseModal();
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userItem) => {
    setSelectedUser(userItem);
    setFormData({
      nama: userItem.nama,
      no_telp: userItem.no_telp,
      password: '',
      role: userItem.role
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const confirmDelete = (userItem) => {
    setUserToDelete(userItem);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('User berhasil dihapus!');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      fetchUsers();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menghapus user');
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Belum pernah login';
    
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Baru saja';
    if (diffMins < 60) return `${diffMins} menit yang lalu`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} jam yang lalu`;
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${hours}.${minutes} ${day}/${month}/${year}`;
  };

  const handleNavigate = (page) => {
    switch (page) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'pengiriman':
        navigate('/pengiriman');
        break;
      case 'status-pengiriman':
        navigate('/status-pengiriman');
        break;
      case 'kelolaakun':
        navigate('/kelolaakun');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Filter users berdasarkan search
  const filteredUsers = (localUsers || []).filter(userItem => 
    userItem.nama.toLowerCase().includes(searchTerm.toLowerCase()) ||
    userItem.no_telp.includes(searchTerm) ||
    userItem.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Header user={user} onMenuToggle={handleToggleSidebar} />
      <div className="dashboard-layout" style={{ marginTop: '60px' }}>
        <Sidebar 
          user={user} 
          onLogout={onLogout}
          currentPage="kelolaakun"
          onNavigate={handleNavigate}
          isOpen={sidebarOpen}
          onToggle={handleToggleSidebar}
        />
        <div className="dashboard-main">
          <div className="dashboard-container">
            <div className="kelola-akun-page">
              <div className="page-header">
                <h1>
                  <i className="bi bi-people-fill me-2"></i>
                  Kelola Akun
                </h1>
                <p>Manajemen akun pengguna sistem </p>
              </div>

              {success && (
                <div className="success-message">
                  <i className="bi bi-check-circle me-2"></i>
                  {success}
                </div>
              )}
              {error && (
                <div className="error-message">
                  <i className="bi bi-x-circle me-2"></i>
                  {error}
                </div>
              )}

              <div className="action-bar">
                <button 
                  className="btn-primary"
                  onClick={handleOpenAddModal}
                  disabled={loading}
                >
                  <i className="bi bi-person-plus-fill me-2"></i>
                  Tambah User Baru
                </button>
              </div>

              <div className="users-list-container">
                <div className="list-header">
                  <h2>Daftar User ({filteredUsers.length})</h2>
                  <div className="search-box">
                    <i className="bi bi-search search-icon"></i>
                    <input 
                      type="text" 
                      placeholder="Cari nama, no telp, atau role..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="users-grid">
                  {filteredUsers.map((u) => (
                    <div key={u.id} className={`user-card ${u.is_online ? 'online' : 'offline'}`}>
                      <div className="user-status-indicator">
                        <span className={`status-dot ${u.is_online ? 'online' : 'offline'}`}></span>
                      </div>
                      <div className="user-avatar">
                        {u.nama.charAt(0).toUpperCase()}
                      </div>
                      <div className="user-info">
                        <h3>{u.nama}</h3>
                        <p className="user-role-badge">
                          <span className={`badge ${u.role}`}>{u.role}</span>
                        </p>
                        <p className="user-phone">
                          <i className="bi bi-telephone-fill me-1"></i>
                          {u.no_telp}
                        </p>
                        <p className="user-status">
                          {u.is_online ? (
                            <span className="status-online">
                              <i className="bi bi-circle-fill me-1" style={{ color: '#28a745' }}></i>
                              Online
                            </span>
                          ) : (
                            <span className="status-offline">
                              <i className="bi bi-circle-fill me-1" style={{ color: '#6c757d' }}></i>
                              {formatLastSeen(u.last_seen)}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="user-actions">
                        <button 
                          className="btn-edit-card"
                          onClick={() => handleEdit(u)}
                          title="Edit User"
                          disabled={loading}
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>
                        <button 
                          className="btn-delete-card"
                          onClick={() => confirmDelete(u)}
                          title="Hapus User"
                          disabled={loading}
                        >
                          <i className="bi bi-trash3"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {filteredUsers.length === 0 && (
                  <div className="empty-state">
                    <i className="bi bi-emoji-frown" style={{ fontSize: '48px', marginBottom: '10px' }}></i>
                    <p>Tidak ada user yang ditemukan</p>
                  </div>
                )}
              </div>
              {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                  <div className="modal-content-form" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h2>
                        {modalMode === 'edit' ? (
                          <>
                            <i className="bi bi-pencil-square me-2"></i>
                            Edit User
                          </>
                        ) : (
                          <>
                            <i className="bi bi-person-plus-fill me-2"></i>
                            Tambah User Baru
                          </>
                        )}
                      </h2>
                      <button className="modal-close" onClick={handleCloseModal}>
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>

                    <div className="modal-body">
                      <form onSubmit={handleSubmit} className="user-form-modal">
                        <div className="form-group">
                          <label>Nama Lengkap *</label>
                          <input
                            type="text"
                            name="nama"
                            value={formData.nama}
                            onChange={handleChange}
                            placeholder="Masukkan nama lengkap"
                            required
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label>No Telepon *</label>
                          <input
                            type="text"
                            name="no_telp"
                            value={formData.no_telp}
                            onChange={handleChange}
                            placeholder="08xxxxxxxxxx"
                            required
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label>
                            Password {modalMode === 'edit' && '(Kosongkan jika tidak ingin mengubah)'}
                            {modalMode === 'add' && ' *'}
                          </label>
                          <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder={modalMode === 'edit' ? "Masukkan password baru" : "Masukkan password"}
                            required={modalMode === 'add'}
                            className="form-input"
                          />
                          {modalMode === 'edit' && (
                            <small className="form-help">Kosongkan jika tidak ingin mengubah password</small>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Role *</label>
                          <select 
                            name="role" 
                            value={formData.role} 
                            onChange={handleChange} 
                            required
                            className="form-select"
                          >
                            <option value="admin">👨‍💼 Admin</option>
                            <option value="kurir">🚚 Kurir</option>
                          </select>
                        </div>

                        <div className="form-actions-modal">
                          <button type="submit" className="btn-submit-modal" disabled={loading}>
                            {loading ? (
                              <>
                                <i className="bi bi-hourglass-split me-2"></i>
                                Menyimpan...
                              </>
                            ) : modalMode === 'edit' ? (
                              <>
                                <i className="bi bi-floppy me-2"></i>
                                Update User
                              </>
                            ) : (
                              <>
                                <i className="bi bi-floppy me-2"></i>
                                Simpan User
                              </>
                            )}
                          </button>
                          <button 
                            type="button" 
                            className="btn-cancel-modal" 
                            onClick={handleCloseModal}
                            disabled={loading}
                          >
                            Batal
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
              {showDeleteConfirm && (
                <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header-delete">
                      <h3>
                        <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#dc3545' }}></i>
                        Konfirmasi Hapus
                      </h3>
                    </div>
                    <div className="modal-body">
                      <p>Apakah Anda yakin ingin menghapus user:</p>
                      <div className="user-delete-info">
                        <strong>{userToDelete?.nama}</strong>
                        <span>{userToDelete?.no_telp}</span>
                        <span className={`badge ${userToDelete?.role}`}>{userToDelete?.role}</span>
                      </div>
                      <p className="warning-text">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Data yang dihapus tidak dapat dikembalikan!
                      </p>
                    </div>
                    <div className="modal-actions">
                      <button className="btn-danger" onClick={handleDelete} disabled={loading}>
                        {loading ? (
                          <>
                            <i className="bi bi-hourglass-split me-2"></i>
                            Menghapus...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-trash3 me-2"></i>
                            Ya, Hapus
                          </>
                        )}
                      </button>
                      <button 
                        className="btn-secondary" 
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={loading}
                      >
                        Batal
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KelolaAkun;